import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';
import MenuBarMenu from './menu-bar-menu.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import dropdownCaret from './dropdown-caret.svg';
import styles from './account-dropdown.css';

/*
 * The account menu shown when the signed-in user's name is clicked.
 *
 * This is a standalone GUI: there is no scratch-www behind it, so the entries
 * do not link to external pages. Each one opens a dialog or performs the action
 * directly, which is why every item is a button with a handler rather than a
 * link with an href.
 */
const messages = defineMessages({
    myProjects: {
        id: 'gui.accountMenu.myProjects',
        defaultMessage: '我的作品',
        description: 'Menu item that opens the saved projects dialog'
    },
    accountSettings: {
        id: 'gui.accountMenu.accountSettings',
        defaultMessage: '账号设置',
        description: 'Menu item that opens the account settings dialog'
    },
    signOut: {
        id: 'gui.menuBar.logout',
        defaultMessage: '退出登录',
        description: 'Menu item that signs the user out'
    },
    menuLabel: {
        id: 'gui.accountMenu.label',
        defaultMessage: '账号菜单',
        description: 'Accessible label for the account menu button'
    },
    adminPanel: {
        id: 'gui.accountMenu.adminPanel',
        defaultMessage: '管理后台',
        description: 'Menu item that opens the administrator screen'
    }
});

const AccountDropdown = ({
    className,
    intl,
    isAdmin,
    isOpen,
    onClose,
    onOpenAccountSettings,
    onOpenAdminPanel,
    onOpenProjects,
    onSignOut,
    username
}) => (
    <div className={classNames(styles.accountDropdown, className)}>
        <button
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-label={intl.formatMessage(messages.menuLabel)}
            className={classNames(styles.accountButton, {[styles.accountButtonOpen]: isOpen})}
            id="account-menu-button"
            type="button"
            onClick={onClose}
        >
            <span className={styles.accountName}>
                {username}
            </span>
            <img
                className={classNames(styles.caret, {[styles.caretOpen]: isOpen})}
                src={dropdownCaret}
            />
        </button>
        <MenuBarMenu
            className={styles.menuWrapper}
            open={isOpen}
            place="left"
            onRequestClose={onClose}
        >
            <span id="account-menu-projects">
                <MenuItem
                    className={styles.menuItem}
                    onClick={onOpenProjects}
                >
                    <FormattedMessage {...messages.myProjects} />
                </MenuItem>
            </span>
            <span id="account-menu-settings">
                <MenuItem
                    className={styles.menuItem}
                    onClick={onOpenAccountSettings}
                >
                    <FormattedMessage {...messages.accountSettings} />
                </MenuItem>
            </span>
            {/* Only rendered for an administrator. This hides the entry point;
                the service independently refuses every admin request from
                anyone else, so the menu is never the thing enforcing access. */}
            {isAdmin ? (
                <span id="account-menu-admin">
                    <MenuItem
                        className={styles.menuItem}
                        onClick={onOpenAdminPanel}
                    >
                        <FormattedMessage {...messages.adminPanel} />
                    </MenuItem>
                </span>
            ) : null}
            <MenuSection>
                <span id="account-menu-signout">
                    <MenuItem
                        className={styles.menuItem}
                        onClick={onSignOut}
                    >
                        <FormattedMessage {...messages.signOut} />
                    </MenuItem>
                </span>
            </MenuSection>
        </MenuBarMenu>
    </div>
);

AccountDropdown.propTypes = {
    className: PropTypes.string,
    intl: intlShape.isRequired,
    isAdmin: PropTypes.bool,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onOpenAccountSettings: PropTypes.func,
    onOpenAdminPanel: PropTypes.func,
    onOpenProjects: PropTypes.func,
    onSignOut: PropTypes.func,
    username: PropTypes.string
};

export default injectIntl(AccountDropdown);
export {AccountDropdown};
